// batch-65 inkoop-kosten (FO-482) — de startvulling van de kostensoorten-stamtabel.
//
// EEN bron voor drie afnemers: de migratie `b65_kostensoort_stamtabel` (INSERT ...
// ON CONFLICT (code) DO NOTHING, gegenereerd uit deze lijst en door een drift-test
// bewaakt), het idempotente seed-script (`packages/db/seed/seed-kostensoorten.ts`,
// maakt alleen ONTBREKENDE codes aan en overschrijft nooit een beheerwijziging) en
// de herkenning-tests. Na de eerste vulling is de tabel leidend: beheer gebeurt in
// Instellingen > Kosten, niet hier.
//
// Codes zijn stabiel en onveranderlijk. De vijftien soorten die de oude enum
// `InkoopKostensoort` vervangen houden die enum-waarde als code (en als
// `legacyEnum`): de backfill is daardoor 1-op-1 en alle bestaande rapportage-
// sleutels (medewerker-, materieel- en trendoverzichten) blijven gelijk, zodat de
// totalen voor en na de migratie aantoonbaar gelijk zijn (FO-482 §9.2).

/** Een doeltype-relevantie (sortering in de keuzelijst; niets wordt geblokkeerd). */
export type KostensoortRelevantieSeed = 'MEDEWERKER' | 'VOERTUIG' | 'DRAGER' | 'KLANT' | 'ALGEMEEN'

/** De vijftien waarden van de oude enum (alleen voor de migratie-mapping). */
export type LegacyKostensoortCode =
  | 'OPLEIDING'
  | 'ELEKTRONICA'
  | 'GEREEDSCHAP'
  | 'KLEDING'
  | 'OVERIG'
  | 'DECLARATIE'
  | 'VEILIGHEID'
  | 'ONDERHOUD_REPARATIE'
  | 'BANDEN'
  | 'BRANDSTOF'
  | 'VERZEKERING'
  | 'LEASE_AFSCHRIJVING'
  | 'KEURING'
  | 'SCHADE_EIGEN_RISICO'
  | 'MODULES_LAYINS'

export interface KostenHoofdgroepSeed {
  code: string
  naam: string
}

export interface KostensoortSeed {
  code: string
  naam: string
  hoofdgroep: string
  relevant: readonly KostensoortRelevantieSeed[]
  zoekwoorden: readonly string[]
  legacyEnum?: LegacyKostensoortCode
  /** Default true; false voor posten zonder aftrekbare btw (boetes, verzekeringen). */
  btwAftrekbaar?: boolean
}

/** De dertien hoofdgroepen in weergavevolgorde (volgorde = index x 10). */
export const KOSTEN_HOOFDGROEPEN_SEED: readonly KostenHoofdgroepSeed[] = [
  { code: 'PERSONEEL', naam: 'Personeelskosten' },
  { code: 'INHUUR', naam: 'Inhuur & uitbesteed werk' },
  { code: 'MATERIALEN', naam: 'Materialen & onderdelen' },
  { code: 'WAGENPARK', naam: 'Wagenpark & vervoer' },
  { code: 'HUISVESTING', naam: 'Huisvesting' },
  { code: 'ICT', naam: 'ICT & communicatie' },
  { code: 'ADMINISTRATIE', naam: 'Administratie en financieel' },
  { code: 'VERZEKERINGEN', naam: 'Verzekeringen' },
  { code: 'JURIDISCH', naam: 'Juridisch & advies' },
  { code: 'KWALITEIT', naam: 'Kwaliteit, veiligheid & certificering' },
  { code: 'COMMERCIEEL', naam: 'Commercieel & marketing' },
  { code: 'KANTOOR', naam: 'Kantoor & algemeen' },
  { code: 'BELASTINGEN', naam: 'Belastingen, heffingen & bijzondere kosten' },
]

/**
 * De kostensoorten per hoofdgroep in weergavevolgorde (volgorde binnen de groep =
 * positie x 10). Zoekwoorden zijn kleine letters; de herkenning normaliseert beide
 * kanten (diacritics, interpunctie) en matcht op woordbegin (korte woorden van drie
 * tekens of minder alleen als heel woord), langste treffer wint.
 */
export const KOSTENSOORTEN_SEED: readonly KostensoortSeed[] = [
  // ── Personeelskosten ─────────────────────────────────────────────────────
  {
    code: 'OPLEIDING',
    naam: 'Opleidingen en cursussen',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['opleiding', 'cursus', 'training', 'examen', 'certificering', 'bhv', 'vca', 'scholing', 'workshop', 'seminar', 'congres', 'e-learning'],
    legacyEnum: 'OPLEIDING',
  },
  {
    code: 'KLEDING',
    naam: 'Werkkleding',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['kleding', 'werkkleding', 'bedrijfskleding', 'werkjas', 'werkbroek', 'jas', 'broek', 'shirt', 'polo', 'trui', 'overall', 'softshell', 'bodywarmer', 'fleece', 'schoen', 'laars'],
    legacyEnum: 'KLEDING',
  },
  {
    code: 'VEILIGHEID',
    naam: "PBM's",
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER', 'DRAGER'],
    zoekwoorden: ['veiligheid', 'pbm', 'helm', 'gehoorbescherming', 'otoplast', 'valbeveiliging', 'veiligheidsbril', 'veiligheidsschoen', 'werkschoen', 'werkhandschoen', 'handschoen', 'stofmasker', 'adembescherming', 'harnas'],
    legacyEnum: 'VEILIGHEID',
  },
  {
    code: 'PERS_REIS_VERBLIJF',
    naam: 'Reis- en verblijfkosten',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER', 'KLANT'],
    zoekwoorden: ['reiskosten', 'hotel', 'overnachting', 'verblijfkosten', 'treinkaart', 'ov chipkaart', 'ns business', 'vliegticket', 'reisbureau'],
  },
  {
    code: 'PERS_LOGIES',
    naam: 'Logies en huisvesting personeel',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER', 'KLANT'],
    zoekwoorden: ['logies', 'huisvesting personeel', 'pension', 'vakantiewoning', 'bungalow', 'recreatiewoning', 'personeelshuisvesting'],
  },
  {
    code: 'PERS_MAALTIJDEN',
    naam: 'Maaltijden en consumpties',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER', 'KLANT'],
    zoekwoorden: ['maaltijd', 'lunch', 'diner', 'catering', 'consumptie', 'broodjes'],
  },
  {
    code: 'PERS_MEDISCH',
    naam: 'Medische keuringen',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['pago', 'periodiek arbeidsgezondheidskundig', 'audiometrie', 'longfunctie', 'medische keuring', 'aanstellingskeuring', 'gezondheidsonderzoek'],
  },
  {
    code: 'PERS_ARBODIENST',
    naam: 'Arbodienst en verzuimbegeleiding',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['arbodienst', 'arbo', 'bedrijfsarts', 'verzuimbegeleiding', 're integratie', 'casemanager'],
  },
  {
    code: 'PERS_WERVING',
    naam: 'Werving en selectie',
    hoofdgroep: 'PERSONEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['werving', 'vacature', 'recruitment', 'recruiter', 'headhunter', 'indeed', 'jobboard', 'personeelsadvertentie'],
  },
  {
    code: 'PERS_ACTIVITEITEN',
    naam: 'Personeelsactiviteiten en attenties',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER', 'ALGEMEEN'],
    zoekwoorden: ['personeelsuitje', 'personeelsfeest', 'kerstpakket', 'attentie', 'bloemen', 'cadeau', 'jubileum', 'bedrijfsuitje'],
  },
  {
    code: 'DECLARATIE',
    naam: 'Overige personeelskosten',
    hoofdgroep: 'PERSONEEL',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['declaratie', 'onkostenvergoeding', 'personeelskosten'],
    legacyEnum: 'DECLARATIE',
  },

  // ── Inhuur & uitbesteed werk ──────────────────────────────────────────────
  {
    code: 'INHUUR_ZZP',
    naam: "Inhuur zzp'ers",
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT', 'ALGEMEEN'],
    zoekwoorden: ['zzp', 'zzper', 'zelfstandige', 'freelance', 'freelancer', 'inhuur'],
  },
  {
    code: 'INHUUR_UITZEND',
    naam: 'Uitzendkrachten',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT', 'ALGEMEEN'],
    zoekwoorden: ['uitzendkracht', 'uitzendbureau', 'uitzenduren', 'randstad', 'tempo team', 'adecco', 'start people', 'olympia'],
  },
  {
    code: 'INHUUR_DETACHERING',
    naam: 'Detachering en inleen',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT', 'ALGEMEEN'],
    zoekwoorden: ['detachering', 'inleen', 'inlening', 'uitlening', 'gedetacheerd'],
  },
  {
    code: 'INHUUR_ONDERAANNEMING',
    naam: 'Onderaanneming',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT'],
    zoekwoorden: ['onderaanneming', 'onderaannemer', 'uitbesteed', 'uitbesteding', 'aanneemsom'],
  },
  {
    code: 'INHUUR_SPECIALIST',
    naam: 'Specialistisch werk derden',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT'],
    zoekwoorden: ['thermografie', 'trillingsmeting', 'trillingsanalyse', 'uitlijnen', 'balanceren', 'specialistisch', 'inspectie derden', 'ndo onderzoek'],
  },
  {
    code: 'INHUUR_STEIGERBOUW',
    naam: 'Steigerbouw',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT'],
    zoekwoorden: ['steiger', 'steigerbouw', 'rolsteiger', 'steigerhuur'],
  },
  {
    code: 'INHUUR_MATERIEEL',
    naam: 'Huur materieel en machines',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT', 'DRAGER'],
    zoekwoorden: ['hoogwerker', 'verreiker', 'schaarlift', 'aggregaat', 'kraan', 'hijskraan', 'machinehuur', 'materieelhuur', 'verhuur', 'boels', 'riwal'],
  },
  {
    code: 'INHUUR_TRANSPORT',
    naam: 'Transport en logistiek derden',
    hoofdgroep: 'INHUUR',
    relevant: ['KLANT', 'ALGEMEEN'],
    zoekwoorden: ['transport', 'transportkosten', 'koerier', 'vracht', 'vrachtkosten', 'verzendkosten', 'pakketdienst', 'postnl', 'dhl', 'ups', 'dpd', 'afleverkosten'],
  },

  // ── Materialen & onderdelen ────────────────────────────────────────────────
  {
    code: 'GEREEDSCHAP',
    naam: 'Gereedschappen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['MEDEWERKER', 'DRAGER'],
    zoekwoorden: ['gereedschap', 'boormachine', 'boor', 'accuboor', 'tang', 'schroevendraaier', 'zaag', 'slijptol', 'haakse slijper', 'multimeter', 'sleutelset', 'bitset', 'dopsleutel', 'waterpas', 'hamer'],
    legacyEnum: 'GEREEDSCHAP',
  },
  {
    code: 'MODULES_LAYINS',
    naam: 'Modules / lay-ins',
    hoofdgroep: 'MATERIALEN',
    relevant: ['DRAGER'],
    zoekwoorden: ['lay in', 'layin', 'module', 'inlay', 'inzetbak', 'schuiminleg'],
    legacyEnum: 'MODULES_LAYINS',
  },
  {
    code: 'MAT_PROJECTMATERIAAL',
    naam: 'Projectmaterialen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['projectmateriaal', 'staal', 'constructiestaal', 'profielstaal', 'plaatwerk', 'plaatstaal', 'rvs', 'aluminium profiel', 'koker', 'hoeklijn'],
  },
  {
    code: 'MAT_KABELS',
    naam: 'Kabels en leidingen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT', 'DRAGER'],
    zoekwoorden: ['kabel', 'installatiedraad', 'vd draad', 'ymvk', 'xmvk', 'vmvk', 'leiding', 'installatiebuis', 'kabelgoot', 'kabelladder', 'flexibele buis'],
  },
  {
    code: 'MAT_INSTALLATIE',
    naam: 'Installatiematerialen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT', 'DRAGER'],
    zoekwoorden: ['installatiemateriaal', 'schakelaar', 'wandcontactdoos', 'lasdoos', 'aansluitdoos', 'wartel', 'kabelschoen', 'klemmenstrook', 'kabelbinder'],
  },
  {
    code: 'MAT_ELEKTROTECHNISCH',
    naam: 'Elektrotechnische componenten',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['relais', 'contactor', 'magneetschakelaar', 'groepenkast', 'verdeelkast', 'zekering', 'installatieautomaat', 'aardlek', 'frequentieregelaar', 'plc', 'voeding', 'sensor', 'motorbeveiliging'],
  },
  {
    code: 'MAT_VERLICHTING',
    naam: 'Verlichting',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['verlichting', 'armatuur', 'led', 'lamp', 'tl buis', 'noodverlichting', 'bouwlamp'],
  },
  {
    code: 'MAT_WERKTUIGBOUWKUNDIG',
    naam: 'Werktuigbouwkundige onderdelen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['lager', 'kogellager', 'afdichting', 'pakking', 'v snaar', 'aandrijfriem', 'pomp', 'ventiel', 'klep', 'afsluiter', 'koppeling', 'tandwiel', 'reductor', 'spie'],
  },
  {
    code: 'MAT_HYDRAULIEK',
    naam: 'Hydrauliek en pneumatiek',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['hydrauliek', 'hydraulische', 'pneumatiek', 'pneumatische', 'slang', 'cilinder', 'perslucht', 'fitting', 'snelkoppeling'],
  },
  {
    code: 'MAT_BEVESTIGING',
    naam: 'Bevestigingsmaterialen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT', 'DRAGER'],
    zoekwoorden: ['bout', 'moer', 'schroef', 'plug', 'sluitring', 'bevestigingsmateriaal', 'draadeind', 'popnagel', 'ankerbout', 'keilbout'],
  },
  {
    code: 'MAT_VERBRUIK',
    naam: 'Verbruiksmaterialen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT', 'DRAGER', 'ALGEMEEN'],
    zoekwoorden: ['verbruiksmateriaal', 'tape', 'isolatietape', 'tie wrap', 'kit', 'siliconen', 'schuurpapier', 'slijpschijf', 'doorslijpschijf', 'lasdraad', 'elektrode', 'poetsdoek'],
  },
  {
    code: 'MAT_SMEERMIDDELEN',
    naam: 'Smeermiddelen en chemie',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT', 'DRAGER'],
    zoekwoorden: ['smeermiddel', 'smeervet', 'kettingvet', 'reiniger', 'ontvetter', 'contactspray', 'wd 40', 'chemie'],
  },
  {
    code: 'MAT_ISOLATIE',
    naam: 'Isolatiematerialen',
    hoofdgroep: 'MATERIALEN',
    relevant: ['KLANT'],
    zoekwoorden: ['isolatie', 'isolatiemateriaal', 'glaswol', 'steenwol', 'leidingisolatie', 'armaflex'],
  },
  {
    code: 'MAT_MEETINSTRUMENTEN',
    naam: 'Meet- en testapparatuur',
    hoofdgroep: 'MATERIALEN',
    relevant: ['MEDEWERKER', 'DRAGER'],
    zoekwoorden: ['meetinstrument', 'testapparatuur', 'installatietester', 'isolatieweerstandsmeter', 'stroomtang', 'spanningzoeker', 'thermometer', 'drukmeter', 'fluke'],
  },
  {
    code: 'MAT_VOORRAAD',
    naam: 'Magazijnvoorraad',
    hoofdgroep: 'MATERIALEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['magazijnvoorraad', 'voorraadaanvulling', 'magazijn'],
  },

  // ── Wagenpark & vervoer ─────────────────────────────────────────────────────
  {
    code: 'BRANDSTOF',
    naam: 'Brandstof',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['brandstof', 'diesel', 'benzine', 'euro95', 'tanken', 'tankbeurt', 'adblue', 'lpg', 'laadsessie', 'laadpaal', 'laadkosten', 'elektrisch laden'],
    legacyEnum: 'BRANDSTOF',
  },
  {
    code: 'WAGEN_TOL',
    naam: 'Tolkosten',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['tol', 'tolweg', 'tolheffing', 'tolkosten', 'tolpoort', 'tunnel', 'vignet', 'peage', 'maut'],
  },
  {
    code: 'WAGEN_PARKEREN',
    naam: 'Parkeerkosten',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG', 'MEDEWERKER'],
    zoekwoorden: ['parkeren', 'parkeerkosten', 'parkeergarage', 'parkeervergunning', 'parkeerplaats', 'q park', 'yellowbrick'],
  },
  {
    code: 'BANDEN',
    naam: 'Banden',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['band', 'banden', 'winterband', 'zomerband', 'allseasonband', 'velg', 'bandenwissel', 'uitlijnen banden'],
    legacyEnum: 'BANDEN',
  },
  {
    code: 'KEURING',
    naam: 'APK / keuringen',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['apk', 'keuring', 'roetmeting', 'tachograaf', 'tachograafkeuring'],
    legacyEnum: 'KEURING',
  },
  {
    code: 'ONDERHOUD_REPARATIE',
    naam: 'Reparatie / onderhoud',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG', 'DRAGER'],
    zoekwoorden: ['onderhoud', 'reparatie', 'beurt', 'onderhoudsbeurt', 'kleine beurt', 'grote beurt', 'olie', 'remmen', 'remblokken', 'distributieriem', 'garage', 'werkplaats', 'accu', 'ruitenwisser', 'ruitenvloeistof'],
    legacyEnum: 'ONDERHOUD_REPARATIE',
  },
  {
    code: 'WAGEN_WASSEN',
    naam: 'Wassen en reinigen',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['wassen', 'wasstraat', 'autowas', 'carwash', 'interieurreiniging'],
  },
  {
    code: 'LEASE_AFSCHRIJVING',
    naam: 'Leasekosten',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['lease', 'leasetermijn', 'operational lease', 'financial lease', 'afschrijving', 'leaseauto'],
    legacyEnum: 'LEASE_AFSCHRIJVING',
  },
  {
    code: 'WAGEN_HUUR',
    naam: 'Huur voertuigen',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG', 'KLANT'],
    zoekwoorden: ['autohuur', 'huurauto', 'huurbus', 'vervangend vervoer', 'sixt', 'hertz', 'europcar'],
  },
  {
    code: 'WAGEN_INRICHTING',
    naam: 'Voertuiginrichting en accessoires',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['bedrijfswageninrichting', 'wageninrichting', 'sortimo', 'bott', 'dakdrager', 'imperiaal', 'trekhaak', 'dashcam', 'autoaccessoire'],
  },
  {
    code: 'WAGEN_FLEETMANAGEMENT',
    naam: 'Fleetmanagement en ritregistratie',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['fleetmanagement', 'ritregistratie', 'ritadministratie', 'track en trace', 'track trace', 'gps tracker'],
  },
  {
    code: 'WAGEN_KAARTKOSTEN',
    naam: 'Tankpas- en kaartkosten',
    hoofdgroep: 'WAGENPARK',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['kaartkosten', 'tankpas', 'pasjeskosten', 'servicekosten tankpas', 'tankpaskosten'],
  },

  // ── Huisvesting ─────────────────────────────────────────────────────────────
  {
    code: 'HUIS_HUUR',
    naam: 'Huur bedrijfsruimte',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['huur pand', 'huur bedrijfsruimte', 'huur kantoor', 'huur werkplaats', 'huur loods', 'servicekosten pand', 'kantoorhuur'],
  },
  {
    code: 'HUIS_ENERGIE',
    naam: 'Energie en water',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['energie', 'elektriciteit', 'stroomkosten', 'aardgas', 'drinkwater', 'vattenfall', 'eneco', 'essent', 'stedin', 'liander', 'vitens', 'netbeheer'],
  },
  {
    code: 'HUIS_SCHOONMAAK',
    naam: 'Schoonmaak',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['schoonmaak', 'schoonmaakdienst', 'glazenwasser', 'schoonmaakmiddelen', 'sanitaire middelen'],
  },
  {
    code: 'HUIS_ONDERHOUD',
    naam: 'Onderhoud gebouw en terrein',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['onderhoud pand', 'klusjesman', 'schilderwerk', 'loodgieter', 'dakdekker', 'groenvoorziening', 'hovenier', 'terreinonderhoud'],
  },
  {
    code: 'HUIS_BEVEILIGING',
    naam: 'Beveiliging en alarm',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['alarm', 'alarmopvolging', 'beveiliging pand', 'bewaking', 'camerabewaking', 'meldkamer', 'toegangscontrole'],
  },
  {
    code: 'HUIS_AFVAL',
    naam: 'Afvalverwerking',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN', 'KLANT'],
    zoekwoorden: ['afval', 'afvalverwerking', 'afvalcontainer', 'container', 'renewi', 'suez', 'milieustraat', 'papierbak'],
  },
  {
    code: 'HUIS_OPSLAG',
    naam: 'Opslag en containers',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['opslagruimte', 'opslagcontainer', 'zeecontainer', 'self storage', 'opslag'],
  },
  {
    code: 'HUIS_INVENTARIS',
    naam: 'Inventaris en inrichting',
    hoofdgroep: 'HUISVESTING',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['meubilair', 'bureaustoel', 'bureau', 'kast', 'inrichting', 'inventaris', 'magazijnstelling', 'stelling'],
  },

  // ── ICT & communicatie ──────────────────────────────────────────────────────
  {
    code: 'ELEKTRONICA',
    naam: 'Hardware',
    hoofdgroep: 'ICT',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['hardware', 'laptop', 'notebook', 'computer', 'tablet', 'ipad', 'monitor', 'beeldscherm', 'oplader', 'elektronica', 'headset', 'toetsenbord', 'muis', 'dockingstation', 'printer'],
    legacyEnum: 'ELEKTRONICA',
  },
  {
    code: 'ICT_MOBIEL',
    naam: 'Mobiele telefoons',
    hoofdgroep: 'ICT',
    relevant: ['MEDEWERKER'],
    zoekwoorden: ['telefoon', 'smartphone', 'iphone', 'samsung galaxy', 'toestel', 'toestelbetaling', 'telefoonhoes', 'screenprotector'],
  },
  {
    code: 'ICT_TELEFONIE',
    naam: 'Telefonie en abonnementen',
    hoofdgroep: 'ICT',
    relevant: ['MEDEWERKER', 'ALGEMEEN'],
    zoekwoorden: ['telefonie', 'telefoonkosten', 'telefoonabonnement', 'mobiel abonnement', 'belbundel', 'databundel', 'simkaart', 'belkosten', 'roaming', 'kpn', 'vodafone', 't mobile', 'odido'],
  },
  {
    code: 'ICT_INTERNET',
    naam: 'Internet en netwerk',
    hoofdgroep: 'ICT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['internet', 'glasvezel', 'wifi', 'netwerk', 'router', 'switch', 'firewall', 'access point'],
  },
  {
    code: 'ICT_SOFTWARE',
    naam: 'Software en licenties',
    hoofdgroep: 'ICT',
    relevant: ['ALGEMEEN', 'MEDEWERKER'],
    zoekwoorden: ['software', 'licentie', 'microsoft 365', 'office 365', 'autocad', 'eplan', 'adobe', 'subscription'],
  },
  {
    code: 'ICT_CLOUD',
    naam: 'Cloud- en SaaS-diensten',
    hoofdgroep: 'ICT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['saas', 'cloud', 'hosting', 'azure', 'aws', 'server', 'domeinnaam', 'webhosting'],
  },
  {
    code: 'ICT_BEHEER',
    naam: 'ICT-beheer en support',
    hoofdgroep: 'ICT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['it support', 'ict beheer', 'systeembeheer', 'helpdesk', 'managed services', 'werkplekbeheer'],
  },
  {
    code: 'ICT_PRINT',
    naam: 'Printen en kopieren',
    hoofdgroep: 'ICT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['toner', 'cartridge', 'inkt', 'kopieerapparaat', 'multifunctional', 'printerlease', 'afdrukken'],
  },

  // ── Administratie en financieel ────────────────────────────────────────────
  {
    code: 'ADM_ACCOUNTANT',
    naam: 'Accountantskosten',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['accountant', 'accountantskosten', 'jaarrekening', 'controleverklaring', 'samenstellingsverklaring'],
  },
  {
    code: 'ADM_BOEKHOUDING',
    naam: 'Administratie en boekhouding',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['administratiekantoor', 'boekhouding', 'boekhouder', 'exact online', 'twinfield', 'administratiekosten'],
  },
  {
    code: 'ADM_SALARIS',
    naam: 'Salarisadministratie',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['salarisadministratie', 'loonadministratie', 'nmbrs', 'loket', 'payroll', 'loonstroken'],
  },
  {
    code: 'ADM_BANK',
    naam: 'Bankkosten',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['bankkosten', 'transactiekosten', 'betaalverkeer', 'rabobank', 'ing bank', 'abn amro', 'pinautomaat'],
  },
  {
    code: 'ADM_FINQLE',
    naam: 'Finqle / financieringsplatform',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['finqle', 'factoring', 'factoringkosten', 'financieringsplatform', 'voorfinanciering'],
  },
  {
    code: 'ADM_INCASSO',
    naam: 'Incasso en debiteurenbeheer',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['incasso', 'incassobureau', 'deurwaarder', 'debiteurenbeheer', 'kredietcheck', 'creditsafe', 'graydon'],
  },
  {
    code: 'ADM_RENTE',
    naam: 'Rente en financieringskosten',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['rente', 'financieringskosten', 'lening', 'afsluitprovisie', 'kredietkosten'],
  },
  {
    code: 'ADM_REGISTRATIES',
    naam: 'KvK en registraties',
    hoofdgroep: 'ADMINISTRATIE',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['kvk', 'kamer van koophandel', 'handelsregister', 'uittreksel', 'registratiekosten'],
  },

  // ── Verzekeringen ────────────────────────────────────────────────────────────
  {
    code: 'VERZEKERING',
    naam: 'Wagenpark',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['VOERTUIG'],
    // FO-482 A2 — "Wagenpark > Voertuigverzekering" is bewust GEEN aparte actieve
    // soort: het is een zoekwoord (alias) van deze soort (beslispunt PO).
    zoekwoorden: ['autoverzekering', 'voertuigverzekering', 'wagenparkverzekering', 'casco', 'wa dekking', 'allrisk voertuig'],
    legacyEnum: 'VERZEKERING',
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_AANSPRAKELIJKHEID',
    naam: 'Aansprakelijkheid (AVB)',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['aansprakelijkheid', 'aansprakelijkheidsverzekering', 'avb', 'bedrijfsaansprakelijkheid', 'beroepsaansprakelijkheid'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_CAR',
    naam: 'CAR- en montageverzekering',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['KLANT', 'ALGEMEEN'],
    zoekwoorden: ['car verzekering', 'construction all risk', 'montageverzekering', 'werkenverzekering'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_GEBOUW',
    naam: 'Opstal en inventaris',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['opstalverzekering', 'opstal', 'inventarisverzekering', 'inboedelverzekering', 'brandverzekering', 'glasverzekering'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_MATERIEEL',
    naam: 'Gereedschap en materieel',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['DRAGER', 'ALGEMEEN'],
    zoekwoorden: ['gereedschapsverzekering', 'materieelverzekering', 'machinebreuk', 'transportverzekering'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_VERZUIM',
    naam: 'Verzuim en arbeidsongeschiktheid',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['verzuimverzekering', 'arbeidsongeschiktheid', 'wga', 'ziekteverzuimverzekering', 'wia'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_RECHTSBIJSTAND',
    naam: 'Rechtsbijstand',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['rechtsbijstand', 'rechtsbijstandverzekering', 'das rechtsbijstand', 'arag'],
    btwAftrekbaar: false,
  },
  {
    code: 'VERZ_OVERIG',
    naam: 'Overige verzekeringen',
    hoofdgroep: 'VERZEKERINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['verzekering', 'polis', 'premie', 'assurantie', 'assurantiebelasting'],
    btwAftrekbaar: false,
  },

  // ── Juridisch & advies ──────────────────────────────────────────────────────
  {
    code: 'JUR_ADVOCAAT',
    naam: 'Advocaat en juridisch advies',
    hoofdgroep: 'JURIDISCH',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['advocaat', 'advocatenkantoor', 'jurist', 'juridisch advies', 'procedure', 'dagvaarding', 'mediation'],
  },
  {
    code: 'JUR_NOTARIS',
    naam: 'Notariskosten',
    hoofdgroep: 'JURIDISCH',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['notaris', 'notariskosten', 'akte', 'statutenwijziging'],
  },
  {
    code: 'JUR_ADVIES',
    naam: 'Bedrijfsadvies en consultancy',
    hoofdgroep: 'JURIDISCH',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['consultancy', 'consultant', 'adviseur', 'advieskosten', 'organisatieadvies', 'interim manager'],
  },
  {
    code: 'JUR_FISCAAL',
    naam: 'Fiscaal advies',
    hoofdgroep: 'JURIDISCH',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['fiscalist', 'belastingadvies', 'fiscaal advies', 'belastingadviseur'],
  },
  {
    code: 'JUR_HR_ADVIES',
    naam: 'HR- en arbeidsrechtelijk advies',
    hoofdgroep: 'JURIDISCH',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['arbeidsrecht', 'hr advies', 'personeelsadvies', 'cao advies', 'arbeidsrechtelijk'],
  },

  // ── Kwaliteit, veiligheid & certificering ──────────────────────────────────
  {
    code: 'KWAL_CERTIFICERING',
    naam: 'Certificering en audits',
    hoofdgroep: 'KWALITEIT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['iso 9001', 'iso', 'vca certificering', 'audit', 'certificatie instelling', 'dekra', 'kiwa', 'tuv', 'bureau veritas', 'scios'],
  },
  {
    code: 'KWAL_KEURING_ARBEIDSMIDDELEN',
    naam: 'Keuring gereedschap en installaties',
    hoofdgroep: 'KWALITEIT',
    relevant: ['DRAGER', 'MEDEWERKER'],
    zoekwoorden: ['nen 3140', 'nen3140', 'keuring gereedschap', 'keuring arbeidsmiddelen', 'keuring ladder', 'keuring hijsmiddelen', 'keuring valbeveiliging', 'e keuring', 'nen 1010'],
  },
  {
    code: 'KWAL_KALIBRATIE',
    naam: 'Kalibratie meetmiddelen',
    hoofdgroep: 'KWALITEIT',
    relevant: ['DRAGER', 'MEDEWERKER'],
    zoekwoorden: ['kalibratie', 'kalibreren', 'ijking', 'kalibratiecertificaat'],
  },
  {
    code: 'KWAL_GASDETECTIE',
    naam: 'Gasdetectie en veiligheidsmeting',
    hoofdgroep: 'KWALITEIT',
    relevant: ['MEDEWERKER', 'DRAGER'],
    zoekwoorden: ['gasdetectie', 'gasmeter', 'multigasmeter', 'o2 meter', 'h2s', 'explosiemeter'],
  },
  {
    code: 'KWAL_EHBO',
    naam: 'EHBO- en BHV-middelen',
    hoofdgroep: 'KWALITEIT',
    relevant: ['ALGEMEEN', 'VOERTUIG'],
    zoekwoorden: ['ehbo', 'bhv middelen', 'verbandtrommel', 'verbandkoffer', 'aed', 'brandblusser', 'blusmiddelen', 'blusdeken'],
  },
  {
    code: 'KWAL_MILIEU',
    naam: 'Milieu en gevaarlijke stoffen',
    hoofdgroep: 'KWALITEIT',
    relevant: ['ALGEMEEN', 'KLANT'],
    zoekwoorden: ['milieu', 'gevaarlijke stoffen', 'asbest', 'sanering', 'adr', 'chemisch afval', 'olieafval'],
  },
  {
    code: 'KWAL_RIE',
    naam: 'Veiligheidskundige en RI&E',
    hoofdgroep: 'KWALITEIT',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['rie', 'ri e', 'risico inventarisatie', 'veiligheidskundige', 'hogere veiligheidskundige', 'toolbox'],
  },

  // ── Commercieel & marketing ─────────────────────────────────────────────────
  {
    code: 'COM_RECLAME',
    naam: 'Reclame en advertenties',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['advertentie', 'reclame', 'google ads', 'linkedin ads', 'social media', 'campagne', 'mediabureau'],
  },
  {
    code: 'COM_WEBSITE',
    naam: 'Website en online',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['website', 'webdesign', 'webbouwer', 'seo', 'online marketing'],
  },
  {
    code: 'COM_DRUKWERK',
    naam: 'Drukwerk',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['drukwerk', 'visitekaartje', 'folder', 'flyer', 'brochure', 'drukkerij'],
  },
  {
    code: 'COM_BELETTERING',
    naam: 'Belettering en promotie',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN', 'VOERTUIG'],
    zoekwoorden: ['belettering', 'autobelettering', 'reclamebord', 'spandoek', 'promotiemateriaal', 'merchandise', 'bedrukking'],
  },
  {
    code: 'COM_BEURZEN',
    naam: 'Beurzen en evenementen',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['beurs', 'beursstand', 'evenement', 'sponsoring', 'standhouder'],
  },
  {
    code: 'COM_REPRESENTATIE',
    naam: 'Relatiegeschenken en representatie',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN', 'KLANT'],
    zoekwoorden: ['relatiegeschenk', 'representatie', 'zakenlunch', 'zakendiner', 'relatie evenement'],
  },
  {
    code: 'COM_ACQUISITIE',
    naam: 'Acquisitie en aanbestedingen',
    hoofdgroep: 'COMMERCIEEL',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['acquisitie', 'leadgeneratie', 'telemarketing', 'tenderplatform', 'aanbesteding', 'tenderned', 'negometrix'],
  },

  // ── Kantoor & algemeen ──────────────────────────────────────────────────────
  {
    code: 'KANT_KANTOORARTIKELEN',
    naam: 'Kantoorartikelen',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['kantoorartikelen', 'kantoorbenodigdheden', 'papier', 'pennen', 'ordner', 'nietmachine', 'envelop', 'post it'],
  },
  {
    code: 'KANT_PORTO',
    naam: 'Porto en verzending',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['porto', 'postzegel', 'frankering', 'aangetekend', 'postbus'],
  },
  {
    code: 'KANT_KANTINE',
    naam: 'Kantine en koffie',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['koffie', 'kantine', 'thee', 'koffieautomaat', 'waterkoeler', 'fruitmand'],
  },
  {
    code: 'KANT_LITERATUUR',
    naam: 'Vakliteratuur en normen',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['vakblad', 'vakliteratuur', 'tijdschrift', 'boeken', 'nen norm', 'normabonnement', 'nen connect'],
  },
  {
    code: 'KANT_CONTRIBUTIES',
    naam: 'Contributies en lidmaatschappen',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['contributie', 'lidmaatschap', 'brancheorganisatie', 'techniek nederland', 'uneto', 'ondernemersvereniging'],
  },
  {
    code: 'KANT_KLEIN_INVENTARIS',
    naam: 'Klein inventaris',
    hoofdgroep: 'KANTOOR',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['klein inventaris', 'keukenapparatuur', 'waterkoker', 'magnetron', 'koelkast'],
  },
  {
    code: 'OVERIG',
    naam: 'Overige algemene kosten',
    hoofdgroep: 'KANTOOR',
    relevant: ['MEDEWERKER', 'VOERTUIG', 'DRAGER', 'ALGEMEEN'],
    // Bewust geen algemene woorden als "diversen": geen treffer = leeg laten
    // (nooit gokken), zodat de regel in de werkvoorraad "zonder kostensoort" komt.
    zoekwoorden: ['algemene kosten', 'overige kosten'],
    legacyEnum: 'OVERIG',
  },

  // ── Belastingen, heffingen & bijzondere kosten ─────────────────────────────
  {
    code: 'SCHADE_EIGEN_RISICO',
    naam: 'Schade / eigen risico',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['VOERTUIG', 'MEDEWERKER'],
    zoekwoorden: ['schade', 'eigen risico', 'ruitschade', 'ruit', 'schadeherstel', 'schadeafhandeling', 'schadeexpert'],
    legacyEnum: 'SCHADE_EIGEN_RISICO',
  },
  {
    code: 'BEL_MRB',
    naam: 'Motorrijtuigenbelasting',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['VOERTUIG'],
    zoekwoorden: ['motorrijtuigenbelasting', 'mrb', 'wegenbelasting'],
    btwAftrekbaar: false,
  },
  {
    code: 'BEL_BOETES',
    naam: 'Boetes en naheffingen',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['VOERTUIG', 'MEDEWERKER'],
    zoekwoorden: ['boete', 'bekeuring', 'cjib', 'verkeersboete', 'naheffing', 'parkeerboete', 'administratiekosten boete'],
    btwAftrekbaar: false,
  },
  {
    code: 'BEL_GEMEENTE',
    naam: 'Gemeentelijke belastingen',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['ozb', 'onroerendezaakbelasting', 'rioolheffing', 'afvalstoffenheffing', 'reclamebelasting', 'precario', 'waterschapsbelasting'],
    btwAftrekbaar: false,
  },
  {
    code: 'BEL_LEGES',
    naam: 'Leges en vergunningen',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['ALGEMEEN', 'VOERTUIG', 'KLANT'],
    zoekwoorden: ['leges', 'vergunning', 'ontheffing', 'omgevingsvergunning', 'rdw', 'kentekenbewijs'],
    btwAftrekbaar: false,
  },
  {
    code: 'BEL_DIEFSTAL',
    naam: 'Diefstal en vermissing',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['ALGEMEEN', 'VOERTUIG', 'DRAGER'],
    zoekwoorden: ['diefstal', 'vermissing', 'inbraak', 'gestolen'],
  },
  {
    code: 'BEL_BIJZONDER',
    naam: 'Bijzondere en eenmalige kosten',
    hoofdgroep: 'BELASTINGEN',
    relevant: ['ALGEMEEN'],
    zoekwoorden: ['bijzondere last', 'eenmalige kosten', 'afboeking', 'oninbaar', 'afwaardering'],
  },
]

/**
 * Oude enum-waarde -> stamtabel-code. Identiek (de vijftien soorten houden hun
 * enum-waarde als code), maar expliciet zodat een toekomstige hernoeming niet stil
 * de backfill breekt; de drift-test bewaakt dat elke waarde precies één seed-rij
 * met `legacyEnum` heeft.
 */
export const LEGACY_KOSTENSOORT_STAMCODE: Readonly<Record<LegacyKostensoortCode, string>> = {
  OPLEIDING: 'OPLEIDING',
  ELEKTRONICA: 'ELEKTRONICA',
  GEREEDSCHAP: 'GEREEDSCHAP',
  KLEDING: 'KLEDING',
  OVERIG: 'OVERIG',
  DECLARATIE: 'DECLARATIE',
  VEILIGHEID: 'VEILIGHEID',
  ONDERHOUD_REPARATIE: 'ONDERHOUD_REPARATIE',
  BANDEN: 'BANDEN',
  BRANDSTOF: 'BRANDSTOF',
  VERZEKERING: 'VERZEKERING',
  LEASE_AFSCHRIJVING: 'LEASE_AFSCHRIJVING',
  KEURING: 'KEURING',
  SCHADE_EIGEN_RISICO: 'SCHADE_EIGEN_RISICO',
  MODULES_LAYINS: 'MODULES_LAYINS',
}
